import {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import {
  Envelope,
  Facebook,
  GeoAlt,
  Globe2,
  Instagram,
  Tiktok,
  Whatsapp,
} from 'react-bootstrap-icons';

import './styles/styles.css';

import type { AxiosError } from 'axios';
import ReactStars from 'react-rating-star-with-type';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { coachHouseCellphone, coachHouseContactPhone } from '../../constants/generalData';
import { GuestPortalContext } from '../../modules/context/guestPortalContextV2';
import {
  type ErrorEnvelop,
  useGuestAuth,
  useMutationRequest,
  useNotifications,
} from '../../modules/hooks';
import { useTransaction } from '../../modules/hooks/useTransaction';
import type { ReservationRatingResponse } from '../../modules/interfaces';
import { feedbackLogger } from '../../modules/logger/loggers';
import { endpoints } from '../../modules/mappers/urls';
import { getTransactionContext } from '../../modules/transaction';
import { formatPropertyAddressPortal } from '../../modules/utils/formatAddress';
import { openSMSApp } from '../../modules/utils/sendMessage';
import {
  getApiErrorMessage,
  getApiRequestId,
  getSuccessRequestId,
} from '../../modules/utils/transform';
import { SocialNetworkCard } from '../Cards/SocialNetworkCard';
import { GuestPortalErrors } from '../Modals/GuestPagesModalsV2/constants/GuestPortalConstants';

const ICON_SIZE = 28;

export const Feedback: FC = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { token, generateAuthToken } = useGuestAuth()!;
  const location = useLocation();

  const { setSimpleToasts } = useNotifications()!;
  const { reservation, reservationRating, loadingReservationRating } =
    useContext(GuestPortalContext)!;

  const { startTransaction, endTransaction } = useTransaction();

  const [ratingValue, setRatingValue] = useState(0);
  const [guestEmail, setGuestEmail] = useState('');

  const [
    { data: ratingResult, loading: loadingRating, error: ratingError },
    updateReservationRating,
  ] = useMutationRequest<ReservationRatingResponse>(
    endpoints.RATING,
    'patch',
    {
      authGuestToken: token,
    },
    { manual: true },
  );

  const socialNetworkIcon = useCallback((networkName: string): ReactNode => {
    switch (networkName.toLocaleLowerCase()) {
      case 'facebook':
        return <Facebook size={ICON_SIZE} className="card-icon" />;
      case 'tiktok':
        return <Tiktok size={ICON_SIZE} className="card-icon" />;
      case 'instagram':
        return <Instagram size={ICON_SIZE} className="card-icon" />;
      default:
        return <Globe2 size={ICON_SIZE} className="card-icon" />;
    }
  }, []);

  const ratingChanged = (rating: number) => {
    startTransaction('rate-stay');
    feedbackLogger.info(
      { ...getTransactionContext(), reservationId, rating },
      'Rating submitted',
    );
    const dataVal = { reservationId, rating, guestEmail };

    setRatingValue(rating || 0);

    updateReservationRating(dataVal);
  };

  useEffect(() => {
    if (reservationRating) {
      setRatingValue(reservationRating);
    }
  }, [reservationRating]);

  useEffect(() => {
    if (!location.state?.guestEmail) {
      navigate(`/guest-portal/${reservationId}`);
    } else {
      setGuestEmail(location.state.guestEmail);
    }
  }, [location]);

  useEffect(() => {
    if (token) return;

    navigate(`/guest-portal/${reservationId}`);
  }, [navigate, reservationId, token]);

  useEffect(() => {
    if (ratingResult) {
      feedbackLogger.info(
        {
          ...getTransactionContext(),
          reservationId,
          requestId: getSuccessRequestId(ratingResult),
        },
        'Rating succeeded',
      );
      endTransaction();
      setSimpleToasts({
        type: 'success',
        message: 'Thank you! Your rating has been submitted.',
        show: true,
        delay: 3500,
      });
    }
  }, [ratingResult]);

  useEffect(() => {
    if (!ratingError) return;
    const axiosError = ratingError as AxiosError<ErrorEnvelop>;
    feedbackLogger.error(
      {
        ...getTransactionContext(),
        reservationId,
        error: axiosError?.message,
        requestId: getApiRequestId(axiosError),
      },
      'Rating failed',
    );
    endTransaction();
    if (
      getApiErrorMessage(ratingError as AxiosError<ErrorEnvelop>) ===
      GuestPortalErrors.JWT_EXPIRED
    ) {
      generateAuthToken({ reservationId });
    }
  }, [ratingError, reservationId]);

  return (
    <>
      <Container className="container-paddings">
        <hr
          style={{
            marginTop: '45px',
          }}
        />
      </Container>
      <Container
        className="container-paddings direction-padding"
        id="guest-portal"
        style={{ height: '6rem' }}
      >
        <Row style={{ paddingLeft: '0px', paddingRight: '0px' }}>
          <Col
            xs={7}
            style={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'center',
            }}
          >
            <GeoAlt size={26} />
            <div className="direction-title">
              {formatPropertyAddressPortal(reservation?.Property)}
            </div>
          </Col>
          <Col
            xs={5}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'right',
            }}
          >
            <Envelope
              onClick={() =>
                openSMSApp(reservation?.Property?.cellphoneNumber || coachHouseCellphone)
              }
              title="Message us"
              size={26}
              className="message-button-icon"
            />

            <Whatsapp
              onClick={(e) => {
                window.location.href = `tel:${
                  reservation?.Property?.phoneNumber || coachHouseContactPhone
                }`;
                e.preventDefault();
              }}
              title="Call us"
              size={26}
              className="message-button-icon"
              style={{ marginLeft: '30px' }}
            />
          </Col>
        </Row>
      </Container>
      <Container className="container-paddings" id="guest-portal">
        <Row className="m-0 justify-content-center w-100 align-items-center">
          <Col lg={6} style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="social-media-title">
              <div>How was your stay?</div>
              <ReactStars
                onChange={ratingChanged}
                value={ratingValue}
                size={40}
                isEdit
                activeColors={
                  loadingRating || loadingReservationRating ? ['gray'] : ['#FFCE00']
                }
              />
            </div>
          </Col>
        </Row>
      </Container>
      <Container
        className="container-paddings"
        id="guest-portal"
        style={{ marginTop: '16px' }}
      >
        <Row className="m-0 justify-content-center w-100 align-items-center">
          <Col lg={6} style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="social-media-title">
              Don&apos;t forget to follow us on Social Media and stay updated
            </div>
          </Col>
        </Row>

        {reservation?.Property?.propertyNetworks &&
          reservation?.Property?.propertyNetworks?.length > 0 &&
          reservation.Property.propertyNetworks.map((item) => (
            <SocialNetworkCard
              title={`${reservation?.Property?.name || 'The Coach House'} ${item.name}`}
              icon={socialNetworkIcon(item.name)}
              socialNetworkLink={item.link}
            />
          ))}
      </Container>
    </>
  );
};
